/**
 * Project detail editor - child of InfoPanel. Has data processing.
 */
import * as JQuery from "jquery";
import { getCRSInfoFromEPSGInfoString } from "../core";
import type { ParsedConstruktedAjax } from "../types/common";
import { BasePanel } from "./BasePanel";
import type { PanelContainer } from "./BasePanel";

interface FormElements {
    projectTitle: HTMLInputElement;
    projectVisibility: HTMLInputElement;
    postPassword?: HTMLInputElement | null;
    lengthUnit: HTMLSelectElement;
    areaUnit: HTMLSelectElement;
    volumeUnit: HTMLSelectElement;
    coordinateSystem: HTMLSelectElement;
    epsgInfoInput: HTMLInputElement;
    projectStatus: HTMLInputElement;
}

interface FormDataType {
    action: string;
    post_id: string | undefined;
    title: string;
    description: string;
    project_visibility: string;
    post_password: string;
    length_unit: string;
    area_unit: string;
    volume_unit: string;
    coordinate_system: string;
    epsg_info: string;
    project_status: string;
}

interface AjaxResponse {
    success: boolean;
    data: {
        message: string;
        title: string;
        description: string;
    };
}

interface ProjectDetailsSnapshot {
    title: string;
    description: string;
    projectVisibility: string;
    postPassword: string;
    lengthUnit: string;
    areaUnit: string;
    volumeUnit: string;
    coordinateSystem: string;
    epsgInfo: string;
    projectStatus: string;
}

export class ProjectDetailEditorPanel extends BasePanel {
    private _container: ReturnType<typeof window.jQuery> | null = null;

    private _CONSTRUKTED_AJAX!: ParsedConstruktedAjax;

    private _previousStatus: string | null = null;

    private _initialSnapshot: ProjectDetailsSnapshot | null = null;

    getId(): string {
        return "project-detail-editor";
    }

    getContainer(): PanelContainer {
        if (!this._container) {
            this._container = window.jQuery("#construkted-popup-project-editor");
        }

        return this._container;
    }

    protected onInit(): void {
        this._CONSTRUKTED_AJAX = window.CONSTRUKTED_AJAX;
        this._refreshPreviousStatus();
        this._bindEvents();
    }

    protected onShow(): void {
        this._handleProjectVisibilityChange();
        this._initialSnapshot = this._takeSnapshot();
    }

    hasUnsavedChanges(): boolean {
        if (!this._initialSnapshot) {
            return false;
        }

        const currentSnapshot = this._takeSnapshot();

        return this._snapshotKey(currentSnapshot) !== this._snapshotKey(this._initialSnapshot);
    }

    discard(): void {
        if (!this._initialSnapshot) {
            return;
        }

        this._applySnapshot(this._initialSnapshot);
        this._initialSnapshot = this._takeSnapshot();
    }

    confirmDiscardChanges(): boolean {
        if (!this.hasUnsavedChanges()) {
            return true;
        }

        const ok = window.confirm("You have unsaved changes. Are you sure you want to leave?");

        if (ok) {
            this.discard();
        }

        return ok;
    }

    private _refreshPreviousStatus(): void {
        const statusInput = document.querySelector('input[name="project-status"]:checked') as HTMLInputElement | null;

        this._previousStatus = statusInput ? statusInput.value : null;
    }

    private _snapshotKey(snapshot: ProjectDetailsSnapshot): string {
        const normalized: ProjectDetailsSnapshot = {
            ...snapshot,
            title: snapshot.title.trim(),
            description: snapshot.description.trim(),
            projectVisibility: snapshot.projectVisibility.trim(),
            postPassword: snapshot.postPassword.trim(),
            lengthUnit: snapshot.lengthUnit.trim(),
            areaUnit: snapshot.areaUnit.trim(),
            volumeUnit: snapshot.volumeUnit.trim(),
            coordinateSystem: snapshot.coordinateSystem.trim(),
            epsgInfo: snapshot.epsgInfo.trim(),
            projectStatus: snapshot.projectStatus.trim()
        };

        return JSON.stringify(normalized);
    }

    private _takeSnapshot(): ProjectDetailsSnapshot {
        const title = (document.getElementById("project-title") as HTMLInputElement | null)?.value ?? "";
        const description = window.tinymce.get("project-description")?.getContent?.() ?? "";
        const projectVisibility =
            document.querySelector<HTMLInputElement>('input[name="project-visibility"]:checked')?.value ?? "";
        const postPassword = (document.getElementById("post-password") as HTMLInputElement | null)?.value ?? "";
        const lengthUnit =
            (document.querySelector('select[name="length-unit"]') as HTMLSelectElement | null)?.value ?? "";
        const areaUnit = (document.querySelector('select[name="area-unit"]') as HTMLSelectElement | null)?.value ?? "";
        const volumeUnit =
            (document.querySelector('select[name="volume-unit"]') as HTMLSelectElement | null)?.value ?? "";
        const coordinateSystem =
            (document.querySelector('select[name="coordinate-system"]') as HTMLSelectElement | null)?.value ?? "";
        const epsgInfo = (document.getElementById("epsg-info-input") as HTMLInputElement | null)?.value ?? "";
        const projectStatus =
            document.querySelector<HTMLInputElement>('input[name="project-status"]:checked')?.value ?? "";

        return {
            title,
            description,
            projectVisibility,
            postPassword,
            lengthUnit,
            areaUnit,
            volumeUnit,
            coordinateSystem,
            epsgInfo,
            projectStatus
        };
    }

    private _applySnapshot(snapshot: ProjectDetailsSnapshot): void {
        const titleInput = document.getElementById("project-title") as HTMLInputElement | null;

        if (titleInput) {
            titleInput.value = snapshot.title;
        }

        const postPasswordInput = document.getElementById("post-password") as HTMLInputElement | null;

        if (postPasswordInput) {
            postPasswordInput.value = snapshot.postPassword;
        }

        const epsgInfoInput = document.getElementById("epsg-info-input") as HTMLInputElement | null;

        if (epsgInfoInput) {
            epsgInfoInput.value = snapshot.epsgInfo;
        }

        const lengthUnit = document.querySelector('select[name="length-unit"]') as HTMLSelectElement | null;

        if (lengthUnit) {
            lengthUnit.value = snapshot.lengthUnit;
        }

        const areaUnit = document.querySelector('select[name="area-unit"]') as HTMLSelectElement | null;

        if (areaUnit) {
            areaUnit.value = snapshot.areaUnit;
        }

        const volumeUnit = document.querySelector('select[name="volume-unit"]') as HTMLSelectElement | null;

        if (volumeUnit) {
            volumeUnit.value = snapshot.volumeUnit;
        }

        const coordinateSystem = document.querySelector('select[name="coordinate-system"]') as HTMLSelectElement | null;

        if (coordinateSystem) {
            coordinateSystem.value = snapshot.coordinateSystem;
        }

        const visibilityRadios = document.querySelectorAll<HTMLInputElement>('input[name="project-visibility"]');

        visibilityRadios.forEach((radio) => {
            radio.checked = radio.value === snapshot.projectVisibility;
        });

        const projectStatusRadios = document.querySelectorAll<HTMLInputElement>('input[name="project-status"]');

        projectStatusRadios.forEach((radio) => {
            radio.checked = radio.value === snapshot.projectStatus;
        });

        const editor = window.tinymce.get("project-description") as any;

        if (editor?.setContent) {
            editor.setContent(snapshot.description);
        }

        this._handleProjectVisibilityChange();
    }

    private _bindEvents(): void {
        const jQuery = window.jQuery;
        const panel = this;

        jQuery(document).on("click", ".close-editor", () => {
            window.panelManager?.closePanel("project-detail-editor");
        });
        jQuery(document).on("click", ".save-project-details", (e: any) => panel._handleSaveProjectDetails(e));
        jQuery(document).on("change", 'input[name="project-visibility"]', () => this._handleProjectVisibilityChange());
    }

    private _handleProjectVisibilityChange(): void {
        const selectedValue = document.querySelector<HTMLInputElement>(
            'input[name="project-visibility"]:checked'
        )?.value;
        const passwordField = document.getElementById("project-password-protection-field") as HTMLElement | null;

        if (!passwordField) {
            console.warn(
                'ProjectDetailEditorPanel._handleProjectVisibilityChange: "#project-password-protection-field" not found'
            );

            return;
        }

        if (selectedValue === "password") {
            passwordField.style.display = "block";
        } else {
            passwordField.style.display = "none";
        }
    }

    private _handleSaveProjectDetails(e: JQuery.TriggeredEvent): void {
        e.preventDefault();

        const button = e.target as HTMLButtonElement;

        if (button.disabled) {
            return;
        }

        button.disabled = true;

        const titleElement = document.getElementById("project-title") as HTMLInputElement;
        const title = titleElement?.value.trim();

        if (!title) {
            alert("Project title is required");
            titleElement?.focus();
            button.disabled = false;

            return;
        }

        const description = window.tinymce.get("project-description")?.getContent().trim();

        if (!description) {
            alert("Project description is required");
            button.disabled = false;

            return;
        }

        const formElements = this._getFormElements();

        if (!formElements) {
            console.error("Required form elements not found");
            button.disabled = false;

            return;
        }

        const epsgInfoInput = document.querySelector('input[id="epsg-info-input"]') as HTMLInputElement;
        const epsgInfo = epsgInfoInput?.value ?? "";

        if (epsgInfo) {
            const crsInfo = getCRSInfoFromEPSGInfoString(epsgInfo);

            if (!crsInfo) {
                alert("Invalid EPSG!");
                button.disabled = false;

                return;
            }
        }

        const formData = this._createFormData(formElements, button.dataset.postId);

        this._submitFormData(formData, button);
    }

    private _getFormElements(): FormElements | null {
        const projectTitle = document.getElementById("project-title") as HTMLInputElement;
        const projectVisibility = document.querySelector(
            'input[name="project-visibility"]:checked'
        ) as HTMLInputElement;
        const postPassword = document.getElementById("post-password") as HTMLInputElement | null;
        const lengthUnit = document.querySelector('select[name="length-unit"]') as HTMLSelectElement;
        const areaUnit = document.querySelector('select[name="area-unit"]') as HTMLSelectElement;
        const volumeUnit = document.querySelector('select[name="volume-unit"]') as HTMLSelectElement;
        const coordinateSystem = document.querySelector('select[name="coordinate-system"]') as HTMLSelectElement;
        const epsgInfoInput = document.querySelector('input[id="epsg-info-input"]') as HTMLInputElement;
        const projectStatus = document.querySelector('input[name="project-status"]:checked') as HTMLInputElement;

        if (
            !projectTitle ||
            !projectVisibility ||
            !lengthUnit ||
            !areaUnit ||
            !volumeUnit ||
            !coordinateSystem ||
            !epsgInfoInput ||
            !projectStatus ||
            !window.tinymce
        ) {
            return null;
        }

        return {
            projectTitle,
            projectVisibility,
            lengthUnit,
            areaUnit,
            volumeUnit,
            coordinateSystem,
            epsgInfoInput,
            projectStatus,
            postPassword
        };
    }

    private _createFormData(elements: FormElements, postId: string | undefined): FormDataType {
        return {
            action: "save_project_details",
            post_id: postId,
            title: elements.projectTitle.value.trim(),
            description: window.tinymce.get("project-description")?.getContent() || "",
            project_visibility: elements.projectVisibility.value,
            post_password: elements.projectVisibility.value === "password" ? elements.postPassword?.value ?? "" : "",
            length_unit: elements.lengthUnit.value,
            area_unit: elements.areaUnit.value,
            volume_unit: elements.volumeUnit.value,
            coordinate_system: elements.coordinateSystem.value,
            epsg_info: elements.epsgInfoInput.value,
            project_status: elements.projectStatus.value
        };
    }

    private _submitFormData(formData: FormDataType, button: HTMLButtonElement): void {
        const panel = this;

        window.jQuery.ajax({
            url: this._CONSTRUKTED_AJAX.ajaxurl,
            type: "POST",
            data: formData,
            success: (response: AjaxResponse) => {
                button.disabled = false;

                if (response.success) {
                    this._initialSnapshot = this._takeSnapshot();
                    alert("Project details saved successfully!");
                    window.panelManager?.closePanelInternal("project-detail-editor");

                    const currentUrl = window.location.href;
                    const baseUrl = currentUrl.split("?")[0];
                    const popup = window.jQuery("#construkted-popup-info");
                    const data = response.data;

                    popup.find("h2 > span:first").text(data.title);
                    popup.find(".post-content").html(data.description);

                    if (panel._previousStatus === "draft" && formData.project_status === "publish") {
                        window.location.reload();
                    } else {
                        window.location.href = baseUrl;
                    }
                } else {
                    alert("Error saving project details");
                }
            },
            error: (error: string) => {
                console.error("Save error:", { error });
                button.disabled = false;
                alert("Error saving project details");
            }
        });
    }
}
